import { addEntity } from "actions/actionEntities";
import { addSelectionFromLov } from "actions/lov.action";
import { getDatatableKey } from "containers/datatable/Datatable";
import { LOADER_TIME_TRIGGER } from "customGlobal";
import { track } from "tracking";
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pojo } from "types/Galaxy";
import { getColumnNameId, LovProps, LOV_DATATABLE_BUTTON_VISISIBLITY } from "./Lov";
import { selectContextAsDetail } from "selectors/context.selector";
import { selectAnyEntityById } from "selectors";
import { FilterBarDefinition, GenericListComponent, LovMapping } from "types/Component";
import { PanierItem } from "./LovComponent";
import Modal from "../Modal/Modal";
import { Trans, useTranslation } from "react-i18next";
import { GenericDatatable } from "composants/datatable";
import { Fa } from "composants/Icon";
import { Scrollbars } from "react-custom-scrollbars";
import { Panier } from "./LovComponent";
import { FilterBar } from "types/Search";
import { convertValue, listToMap } from "utils/entities.utils";
import { GSBuilder, initRsqlCriteria, mapToRSQL } from "utils/query.utils";
import { Operators, RSQLCriteria, RSQLFilterExpression } from "rsql-criteria-typescript";
import { getLovColumn, getLovData, getLovDefinition } from "api/lov";
import { get } from "lodash-es";
import { DatatableSort } from "composants/datatable/Table";
import { arrayMove } from "react-sortable-hoc";
import { tw } from "twind";
import { preRecord } from "api/entities";
import GroupComponent from "composants/group/GroupComponent";
import { ExtensionValue } from "composants/satellite/TabExtension";
import { useQuery } from "react-query";
import { fetchExtensionParGroupe } from "api/satellites";
import { ComponentGroupState } from "types/ComponentGroup";
import Autocomplete from "composants/autocomplete/AutoComplete";
import Checkbox from "composants/checkbox/Checkbox";

const EMPTY_GROUP: ComponentGroupState = {
  groupLabel: "",
  groupSize: 0,
  position: 0,
  compos: [],
  datatableTableName: "",
  datatableCtrlKey: ""
};

// Récupération des définitions d'extensions par famille
async function getExtensionsDefinition(
  sjmoCode: string,
  groupeId?: string
): Promise<ComponentGroupState> {
  const result = { ...EMPTY_GROUP };
  if (!groupeId) {
    return EMPTY_GROUP;
  }
  const { data } = await fetchExtensionParGroupe({
    sjmoCode,
    extensionGroupeId: groupeId
  });
  result.compos = data;
  return result;
}

const LovExtension: React.FC<LovProps> = ({
  sjmoCode,
  targetTableName,
  targetId,
  targetCtrlKey,
  lovType,
  column,
  tableName,
  contextTableName,
  contextId,
  syjLovId,
  onClose
}) => {
  const { t } = useTranslation();
  const [definition, setDefinition] = useState<{
    lovId: string | null;
    mappings: LovMapping[];
    filters: FilterBarDefinition[];
    extensionTable: string | null;
  }>({
    lovId: null,
    mappings: [],
    filters: [],
    extensionTable: null
  });

  const [preRecordFailed, setPreRecordFailed] = useState(false);
  const [entityToClone, setEntityToClone] = useState<Pojo | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<Pojo[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState<Record<string, Pojo | "LOADING_POJO">>({});
  const [filterBar, setFilterBar] = useState<FilterBar>({
    filterBarId: null,
    startDate: null,
    endDate: null
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [columns, setColumns] = useState<GenericListComponent[]>([]);

  const [{ extensionGroupe, extensionTable }, setExtension] = useState<{
    extensionGroupe: string | undefined;
    extensionTable: string | undefined;
  }>({ extensionGroupe: undefined, extensionTable: undefined });
  const [filterByExtensionGroup, setFilterByExtensionGroup] = useState(false);
  const [extensions, setExtensions] = useState<ExtensionValue[]>([]);
  const [manualFilter, setManualFilter] = useState<RSQLCriteria | null>(null);

  const gridRef = useRef<GenericDatatable>(null);
  const isValidating = useRef(false);
  const selectionUnique = useMemo(() => lovType === "GS", [lovType]);

  const dispatch = useDispatch();
  const interactions = useSelector((state: any) =>
    selectContextAsDetail(state.interactions, {
      sjmoCode: sjmoCode,
      tableName: targetTableName,
      ctrlKey: targetCtrlKey
    })
  );
  const targetEntity = useSelector((state: any) =>
    selectAnyEntityById(state, {
      sjmoCode: sjmoCode,
      tableName: targetTableName,
      ctrlKey: targetCtrlKey,
      id: targetId || ""
    })
  );
  const targetEntityOrigin = useMemo(() => (targetEntity ? targetEntity.from : "ENTITIES"), [
    targetEntity
  ]);

  useEffect(() => {
    if (lovType === "DT") {
      preRecord({
        sjmoCode,
        tableName: targetTableName,
        context: interactions
      })
        .then(res => {
          setEntityToClone(res.data);
        })
        .catch(() => {
          console.error("error pre-record of table orig during lov initialization");
          setEntityToClone(null);
          setPreRecordFailed(true);
        });
    }
    getLovDefinition(sjmoCode, column, tableName, syjLovId)
      .then(res => {
        setDefinition({
          lovId: res.data.id as any,
          mappings: res.data.mappings,
          filters: res.data.filters,
          extensionTable: res.data.extensionTable
        });
      })
      .catch(() => console.error("error during fetch of lov definition"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (definition.lovId) {
      getLovColumn(definition.lovId as any)
        .then(res => setColumns(res.data.map(col => ({ ...col, compoVisible: true }))))
        .catch(() => console.error("error during feth of lov columns"));
    }
  }, [definition.lovId]);

  // ---------------------------------------------------------------------------
  // Extension
  // ---------------------------------------------------------------------------

  const onChangeGroupe = useCallback((itemChange: Pojo | null, name: string | undefined) => {
    setExtension({
      extensionGroupe: itemChange?.extensionGroupeId,
      extensionTable: itemChange?.id
    });
  }, []);

  const { data: extDefinition } = useQuery({
    queryKey: ["lovExtension", extensionGroupe],
    queryFn: () => getExtensionsDefinition(sjmoCode, extensionGroupe),
    enabled: extensionGroupe !== null,
    onSuccess: data => {
      const ext: ExtensionValue[] = data.compos.map(c => {
        return {
          id: c.id,
          labelFormatter: "",
          unite: "",
          style: "",
          columnName: c.column,
          dataType: c.typeCompo === "CH" ? "C" : "I",
          value: null
        };
      });
      setExtensions(ext);
    }
  });

  const onChange = useCallback(
    (e: SyntheticEvent<any>) => {
      const field: string = e.currentTarget.name;
      const value = convertValue(e);

      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map(ext => {
        let newValue = value;
        if (ext.dataType === "C") {
          newValue = value === true ? "O" : "N";
        }

        if (ext.columnName === field) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
    },
    [extensions]
  );

  const onValueChange = useCallback(
    (field: string | undefined, value: any) => {
      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map(ext => {
        let newValue = value;
        if (ext.dataType === "C") {
          newValue = value === true ? "O" : "N";
        }

        if (ext.columnName === field) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
    },
    [extensions]
  );

  const onItemChange = useCallback(
    (pojo: Pojo, name: string) => {
      let currentExtensions = [...extensions];
      currentExtensions = currentExtensions.map(ext => {
        let newValue = pojo?.id;

        if (ext.columnName === name) {
          return { ...ext, value: newValue };
        } else {
          return ext;
        }
      });
      setExtensions(currentExtensions);
    },
    [extensions]
  );

  const extensionValues = useMemo(() => {
    const values = {};
    const _style = {};
    extensions.forEach((v: ExtensionValue) => {
      values[v.columnName] = v.value;
      if (v.style) {
        _style[v.columnName] = v.style;
      }
    });
    values["_style"] = _style;
    return values;
  }, [extensions]);

  // ---------------------------------------------------------------------------
  // Gestion de la donnée dans la table
  // ---------------------------------------------------------------------------

  const fetchLovData = useCallback(
    (first: number, size: number, filter: string, reset: boolean) => {
      if (definition.lovId) {
        getLovData({
          lovId: definition.lovId,
          first,
          size,
          filter,
          context: "",
          extensions: extensionValues,
          extensionGroup: filterByExtensionGroup ? extensionGroupe : undefined
        }).then(res => {
          const newEntities = reset
            ? listToMap(res.data.data, first)
            : { ...entities, ...listToMap(res.data.data, first) };
          setEntities(newEntities as Record<string, Pojo | "LOADING_POJO">);
          setTotalRecords(res.data.meta.totalRecords);
        });
      }
    },
    [definition.lovId, extensionValues, filterByExtensionGroup, extensionGroupe, entities]
  );

  const fetchData = useCallback(
    (first = 0, size = 10, filter: RSQLCriteria = initRsqlCriteria(), reset: boolean = false) => {
      if (!filter) filter = initRsqlCriteria();
      const columnName = getColumnNameId(definition.lovId, columns);

      // Ajout du critère en not in [] en fonction des selections
      let notIn: string[] = [];
      for (let entity of selectedEntities) {
        if (entity) {
          notIn.push(entity[columnName]);
        }
      }
      if (notIn.length > 0) {
        filter.filters.and(new RSQLFilterExpression(columnName, Operators.NotIn, notIn));
      }

      let contextParameter =
        contextTableName && contextId
          ? `&contextTableName=${contextTableName}&contextId=${contextId}`
          : "";

      let moduleParameter = `&sjmoCode=${sjmoCode}`;

      let filterBarParameter = "";
      if (filterBar) {
        if (filterBar.filterBarId) {
          filterBarParameter = `&filterBarId=${filterBar.filterBarId}`;
        }

        if (filterBar.startDate) {
          filterBarParameter += `&filterBarStart=${encodeURIComponent(filterBar.startDate)}`;
        }

        if (filterBar.endDate) {
          filterBarParameter += `&filterBarEnd=${encodeURIComponent(filterBar.endDate)}`;
        }
      }
      if (definition.lovId) {
        fetchLovData(
          first,
          size,
          filter.build() + filterBarParameter + contextParameter + moduleParameter,
          reset
        );
      }
    },
    [
      definition.lovId,
      columns,
      contextTableName,
      contextId,
      sjmoCode,
      filterBar,
      selectedEntities,
      fetchLovData
    ]
  );

  const loadMoreData = useCallback(
    (p: { startIndex: number; stopIndex: number }) => {
      return new Promise<void>(resolve => {
        fetchData(p.startIndex, p.stopIndex - p.startIndex, manualFilter ?? undefined);
        resolve();
      });
    },
    [fetchData, manualFilter]
  );

  const updateSearch = useCallback(
    (filter?: Record<string, any>, sort?: Record<string, DatatableSort>): Promise<void> => {
      return new Promise<void>(resolve => {
        const likeFilter = filter ? {} : undefined;
        filter &&
          likeFilter &&
          Object.keys(filter).forEach(k => (likeFilter[k] = `%${filter[k]}%`));
        const rsql = mapToRSQL(likeFilter, sort);
        setManualFilter(rsql);
        fetchData(0, 10, rsql, true);
        resolve();
      }).then(() => {
        gridRef.current && gridRef.current.recomputeGrids();
      });
    },
    [fetchData]
  );

  /**
   * Chargement initial des données
   */
  useEffect(() => {
    fetchData(0, 10, manualFilter ?? undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition.lovId]);

  const refreshDataFromTable = useCallback(() => {
    gridRef.current?.refresh();
  }, []);

  const changeFilterBar = useCallback(
    (name: string, value: any) => {
      setFilterBar(prev => ({
        ...prev,
        [name]: value
      }));
      refreshDataFromTable();
    },
    [refreshDataFromTable]
  );

  // ---------------------------------------------------------------------------
  // Gestion de la sélection dans la table
  // ---------------------------------------------------------------------------

  const onRowSelect = useCallback(
    (rowIndex: number | "ALL") => {
      let newSelectedRows: number[] = [];
      if (selectionUnique && rowIndex === "ALL") {
        newSelectedRows = [0];
      } else if (rowIndex === "ALL") {
        // on sélectionne tous les ids
        for (let i = 0; i < Object.keys(entities).length; i++) {
          newSelectedRows.push(i);
        }
      } else if (selectionUnique && selectedRows.indexOf(rowIndex) === -1) {
        newSelectedRows = [rowIndex];
      } else if (selectedRows.indexOf(rowIndex) === -1) {
        newSelectedRows = selectedRows.concat(rowIndex);
      }

      setSelectedRows(newSelectedRows);
      gridRef.current && gridRef.current.recomputeHeader();
    },
    [selectionUnique, selectedRows, entities]
  );

  const onRowUnselect = useCallback(
    (rowIndex: number | "ALL") => {
      let newSelectedRows: number[] = [];
      if (rowIndex !== "ALL") {
        newSelectedRows = selectedRows.filter(row => row !== rowIndex);
      }

      setSelectedRows(newSelectedRows);
      gridRef.current && gridRef.current.recomputeHeader();
    },
    [selectedRows]
  );

  const addToPanier = useCallback(() => {
    if (selectionUnique && selectedEntities.length >= 1) {
      return;
    }

    const newSelectedEntities: Pojo[] = [];
    for (const row of selectedRows) {
      const curr = entities[row] as Pojo | "LOADING_POJO";
      if (curr && curr !== "LOADING_POJO") {
        newSelectedEntities.push(curr);
      }
    }

    setSelectedEntities(se => [...se, ...newSelectedEntities]);
    setSelectedRows([]);
  }, [selectionUnique, selectedEntities.length, selectedRows, entities]);

  const onReorderEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
      setSelectedEntities(prev => arrayMove(prev, oldIndex, newIndex));
    },
    []
  );

  const removeRow = useCallback(
    (index: number) => () => {
      setSelectedEntities(prev => {
        const newEntities = [...prev];
        newEntities.splice(index, 1);
        return newEntities;
      });
      refreshDataFromTable();
    },
    [refreshDataFromTable]
  );

  const clearSelections = useCallback(() => {
    setSelectedEntities([]);
    refreshDataFromTable();
  }, [refreshDataFromTable]);

  // ---------------------------------------------------------------------------
  // Validation du panier
  // ---------------------------------------------------------------------------

  const dispatchToEntity = useCallback(
    (pojo: Pojo) => {
      let eventName: string | null = null;

      if (targetEntityOrigin === "DATATABLE") {
        let dtKey = getDatatableKey(sjmoCode, targetCtrlKey, targetTableName);
        eventName = `${dtKey}--lov`;
      } else if (targetEntityOrigin === "ENTITIES") {
        dispatch(addEntity(sjmoCode, targetTableName, targetId as string, pojo, true));
      }

      if (eventName != null) {
        let customEvent = new CustomEvent<{ pojo: Pojo }>(eventName, { detail: { pojo: pojo } });
        window.dispatchEvent(customEvent);
      }
    },
    [dispatch, sjmoCode, targetCtrlKey, targetEntityOrigin, targetId, targetTableName]
  );

  const validateSelections = useCallback(() => {
    if (preRecordFailed) {
      return;
    }

    if (isValidating.current) {
      return;
    }

    isValidating.current = true;
    const timer = setTimeout(() => {
      setLoading(true);
    }, LOADER_TIME_TRIGGER());

    dispatch(
      addSelectionFromLov({
        entityToClone: lovType === "GS" ? targetEntity : entityToClone,
        sjmoCode: sjmoCode,
        tableOrig: targetTableName,
        target: targetCtrlKey,
        isDatatable: !selectionUnique,
        mappings: definition.mappings || [{ columnTarget: column, columnOrig: "id", wvi: true }],
        selectionToAdd: selectedEntities,
        // on ne remplace pas les uuids lorsque l'on est sur une GS car on modifie seulement l'entité d'origine
        replaceUuid: lovType === "GS" ? false : true,
        dispatchToEntity
      })
    )
      .then(() => {
        isValidating.current = false;
        clearTimeout(timer);
        setLoading(false);
      })
      .then(() => {
        track("lov::confirmed");
        onClose();
      })
      .catch(() => {
        isValidating.current = false;
        clearTimeout(timer);
        setLoading(false);
      });
  }, [
    preRecordFailed,
    dispatch,
    lovType,
    targetEntity,
    entityToClone,
    sjmoCode,
    targetTableName,
    targetCtrlKey,
    selectionUnique,
    definition.mappings,
    column,
    selectedEntities,
    dispatchToEntity,
    onClose
  ]);

  // ---------------------------------------------------------------------------
  // Affichage
  // ---------------------------------------------------------------------------

  return (
    <Modal
      show
      title={
        <Trans
          i18nKey={selectionUnique ? "lov:lov_title_unique" : "lov:lov_title_multiple"}
          values={{ name: tableName }}
        >
          Sélection : {{ name: tableName }}
        </Trans>
      }
      onClose={() => {
        onClose();
        track("lov::cancel");
      }}
      minHeight="90vh"
      minWidth="90vw"
    >
      <div className={tw("flex flex-row gap-4 items-center mb-4")}>
        <button
          className={tw(
            "bg-blue-600 hover:bg-blue-700 text-white pl-4 pr-5 py-2 rounded-3xl my-auto"
          )}
          onClick={addToPanier}
          disabled={preRecordFailed || (selectionUnique && selectedEntities.length > 0)}
        >
          <span className={tw("mr-2")}>
            <Fa icon="plus" fixedWidth />
          </span>
          <span>
            <Trans i18nKey="commun_ajouter">Ajouter</Trans>
          </span>
        </button>
        {definition.extensionTable && (
          <>
            <div className={tw("my-auto")}>{t("commun_lov_filtrer_par")}</div>
            <Autocomplete
              sjmoCode={sjmoCode}
              tableName="extensionTable"
              joinTableName="extensionTable"
              joinListFields="extensionGroupeId.exgrLibelle;extensionGroupeId.exgrCode"
              value={extensionTable}
              onItemChange={onChangeGroupe}
              additionalClause={GSBuilder.AND(
                GSBuilder.Comparison("extensionGroupeId", "OPER_NOT_NULL", ""),
                GSBuilder.Comparison("sysTableId.id", "OPER_EQ", definition.extensionTable),
                GSBuilder.Comparison("extensionGroupeId.exgrLibelle", "OPER_LIKE_ANYWHERE", "")
              )}
            />
            <div className={tw("my-auto ml-3")}>{t("commun_lov_filtrer_par_groupe_extension")}</div>
            <Checkbox
              label={""}
              value={filterByExtensionGroup}
              onValueChange={() => setFilterByExtensionGroup(!filterByExtensionGroup)}
            />
          </>
        )}
      </div>

      {extensionGroupe && (
        <div className={tw("mt-2")}>
          <GroupComponent
            title={extDefinition?.groupLabel}
            group={extDefinition ?? EMPTY_GROUP}
            groupSize={12}
            entity={extensionValues}
            wviState={{}}
            onChange={onChange}
            onValueChange={onValueChange}
            onItemChange={onItemChange}
            onBlur={() => {
              // rien
            }}
            contextMenu={() => {
              /* rien */
            }}
            sjmoCode={sjmoCode}
            isMultipleColumn
          />
        </div>
      )}

      <div className={tw("flex")}>
        <div className={tw("w-8/12 pr-2")}>
          <div
            style={{
              height: 600,
              borderRadius: "var(--datatable-radius)",
              boxShadow: "hsl(0,0%,89%) 0 0 15px 1px"
            }}
          >
            <GenericDatatable
              ref={gridRef}
              toolbarButtonVisibility={LOV_DATATABLE_BUTTON_VISISIBLITY}
              sjmoCode="LOV_GENERIC_TABLE"
              tableName="listeGenerique"
              tableCtrlKey="listeGeneriqueLov"
              selectionActive
              selectedRows={selectedRows}
              onRowSelect={onRowSelect}
              onRowUnselect={onRowUnselect}
              filterBar={filterBar}
              onChangeFilterBar={changeFilterBar}
              loadMoreData={loadMoreData}
              updateSearch={updateSearch}
              entities={entities}
              originalColumns={columns}
              columnWidthName="width"
              totalRecords={totalRecords}
              satellites={[]}
              showActionColumn={false}
              flagDirtyEntities={false}
              filterVisible
              filters={definition.filters}
            />
          </div>
        </div>
        <div className={tw("w-4/12 pl-2")}>
          <Panier
            loading={loading}
            axis="y"
            lockAxis="y"
            useDragHandle
            onSortEnd={onReorderEnd}
            onClear={clearSelections}
            onValidate={validateSelections}
            disabled={preRecordFailed}
          >
            <Scrollbars
              autoHide
              className={tw("min-h-[500px] border-l-1 border-r-1 border-b-1 border-gray-300")}
            >
              {selectedEntities.map((entity, index) => (
                <PanierItem
                  key={index}
                  index={index}
                  title={
                    entity
                      ? get(entity, "label", entity[getColumnNameId(definition.lovId, columns)])
                      : ""
                  }
                  removeRow={removeRow(index)}
                />
              ))}
            </Scrollbars>
          </Panier>
        </div>
      </div>
    </Modal>
  );
};

export default LovExtension;
